'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { Check, ChevronDown, Sparkles, Zap, Building2, type LucideIcon } from 'lucide-react'
import { createClient } from '@/lib/supabase/client'
import { RezaroLogo } from '@/app/(public)/components/rezaro-logo'
import { lp, type SiteLang } from '../_home/locale-path'

type SelectedPlan = 'starter' | 'pro' | 'enterprise'
type SelectedBilling = 'monthly' | 'yearly'

interface PlanCard {
  id: SelectedPlan
  name: string
  monthly: number
  tagline: string
  icon: LucideIcon
}

// Per-locale copy. German ('de') strings are byte-identical to the original
// so the live /signup page is unchanged. English + Turkish are professional
// equivalents. Only visible text is externalized — all form logic, field
// names, Stripe/plan handling and classNames stay the same.
const T = {
  de: {
    planTaglines: { starter: 'Für den ruhigen Anfang', pro: 'Für den vollen Betrieb', enterprise: 'Für mehrere Häuser' },
    socialGoogle: 'Mit Google fortfahren',
    features: [
      '7 Tage voller Zugriff',
      'Kreditkarte hinterlegen, jederzeit kündbar',
      'Nach 7 Tagen automatische Abbuchung',
      'Reservierungen automatisch annehmen',
    ],
    defaultSlug: 'mein-restaurant',
    demoRestaurant: 'demo-restaurant',
    cancelledError: 'Die Zahlung wurde abgebrochen. Sie können den Test mit dem gewählten Plan jederzeit erneut starten.',
    errConsentOAuth: 'Bitte stimmen Sie zuerst den AGB, der Datenschutzerklärung und dem AVV zu.',
    errOAuthFailed: 'OAuth fehlgeschlagen',
    errCheckoutFailed: 'Checkout konnte nicht gestartet werden',
    errCheckout: 'Checkout-Fehler',
    errConsent: 'Bitte stimmen Sie den AGB, der Datenschutzerklärung und dem Auftragsverarbeitungsvertrag zu.',
    errB2B: 'Bitte bestätigen Sie, dass Sie als Unternehmer (Restaurantbetreiber) handeln.',
    errSignupFailed: 'Registrierung fehlgeschlagen',
    errEmailExists: 'Diese E-Mail ist bereits registriert.',
    brandHeading1: 'Die Managementlösung',
    brandHeading2: 'für Ihr Restaurant.',
    brandSub: 'Reservierungen, Bestellungen und Personal — in einer Plattform.',
    planSuffix: '-Plan',
    change: 'Ändern',
    perMonth: '/Monat',
    perMonthYearly: ' (jährlich)',
    trialBlurb: '7 Tage kostenlos testen. Karte wird beim Checkout hinterlegt, aber erst am 8. Tag belastet.',
    footerCopy: '© Rezaro',
    alreadyAccount: 'Bereits ein Konto?',
    signIn: 'Anmelden',
    stepLabel: 'Registrierung',
    formHeading1: 'Erzählen Sie uns von',
    formHeading2: 'Ihrem Restaurant.',
    formSub: 'Konto in wenigen Schritten erstellen.',
    preConsentPre: 'Ich stimme den',
    preConsentMid: 'der',
    preConsentAnd: 'und dem',
    preConsentPost: 'zu.',
    socialDisabledTitle: 'Bitte zuerst AGB akzeptieren',
    or: 'oder',
    emailCta: 'Mit E-Mail registrieren',
    emailLabel: 'E-Mail-Adresse',
    emailPlaceholder: 'ihre@email.de',
    passwordLabel: 'Passwort',
    passwordHint: '(min. 8 Zeichen)',
    restaurantNameLabel: 'Restaurantname',
    restaurantNamePlaceholder: 'Name des Restaurants',
    addressLabel: 'Restaurantadresse',
    addressPlaceholder: 'Adresse, Stadt',
    phoneLabel: 'Telefon',
    phonePlaceholder: '030 12345678',
    websiteLabel: 'Restaurant-Website',
    websiteHint: '(optional)',
    websitePlaceholder: 'www.meinrestaurant.de',
    consentPre: 'Ich habe die',
    consentMid1: 'die',
    consentMid2: 'den',
    consentMid3: 'und die',
    consentPost: 'gelesen und stimme diesen zu.',
    b2bPre: 'Ich bestätige, dass ich als',
    b2bStrong: 'Unternehmer (Restaurantbetreiber)',
    b2bPost: 'handle. Rezaro ist ein B2B-Dienst — ein Widerrufsrecht nach §§ 312g, 355 BGB besteht daher nicht. Stattdessen gilt die 7-tägige kostenlose Testphase.',
    submitLoading: 'Konto wird erstellt...',
    submit: 'Konto kostenlos erstellen',
    submitBlurb: 'Erst nach Wahl eines kostenpflichtigen Plans und Bestätigung mit dem Button „Kostenpflichtig bestellen" wird Ihre Karte belastet. Die Konto-Erstellung ist kostenfrei.',
  },
  en: {
    planTaglines: { starter: 'For a calm start', pro: 'For full operation', enterprise: 'For multiple locations' },
    socialGoogle: 'Continue with Google',
    features: [
      'Full access for 7 days',
      'Add a credit card, cancel anytime',
      'Automatic charge after 7 days',
      'Accept reservations automatically',
    ],
    defaultSlug: 'my-restaurant',
    demoRestaurant: 'demo-restaurant',
    cancelledError: 'The payment was cancelled. You can restart the trial with the selected plan at any time.',
    errConsentOAuth: 'Please first accept the Terms, the Privacy Policy and the DPA.',
    errOAuthFailed: 'OAuth failed',
    errCheckoutFailed: 'Checkout could not be started',
    errCheckout: 'Checkout error',
    errConsent: 'Please accept the Terms, the Privacy Policy and the Data Processing Agreement.',
    errB2B: 'Please confirm that you are acting as a business (restaurant operator).',
    errSignupFailed: 'Registration failed',
    errEmailExists: 'This email is already registered.',
    brandHeading1: 'The management solution',
    brandHeading2: 'for your restaurant.',
    brandSub: 'Reservations, orders and staff — in one platform.',
    planSuffix: ' plan',
    change: 'Change',
    perMonth: '/month',
    perMonthYearly: ' (billed yearly)',
    trialBlurb: 'Try free for 7 days. Your card is stored at checkout but only charged on day 8.',
    footerCopy: '© Rezaro',
    alreadyAccount: 'Already have an account?',
    signIn: 'Sign in',
    stepLabel: 'Registration',
    formHeading1: 'Tell us about',
    formHeading2: 'your restaurant.',
    formSub: 'Create your account in just a few steps.',
    preConsentPre: 'I agree to the',
    preConsentMid: ', the',
    preConsentAnd: 'and the',
    preConsentPost: '.',
    socialDisabledTitle: 'Please accept the Terms first',
    or: 'or',
    emailCta: 'Sign up with email',
    emailLabel: 'Email address',
    emailPlaceholder: 'you@email.com',
    passwordLabel: 'Password',
    passwordHint: '(min. 8 characters)',
    restaurantNameLabel: 'Restaurant name',
    restaurantNamePlaceholder: 'Name of the restaurant',
    addressLabel: 'Restaurant address',
    addressPlaceholder: 'Address, city',
    phoneLabel: 'Phone',
    phonePlaceholder: '030 12345678',
    websiteLabel: 'Restaurant website',
    websiteHint: '(optional)',
    websitePlaceholder: 'www.myrestaurant.com',
    consentPre: 'I have read the',
    consentMid1: ', the',
    consentMid2: ', the',
    consentMid3: 'and the',
    consentPost: 'and agree to them.',
    b2bPre: 'I confirm that I am acting as a',
    b2bStrong: 'business (restaurant operator)',
    b2bPost: '. Rezaro is a B2B service — a right of withdrawal under §§ 312g, 355 BGB therefore does not apply. The 7-day free trial applies instead.',
    submitLoading: 'Creating account...',
    submit: 'Create your account for free',
    submitBlurb: 'Your card is only charged after you choose a paid plan and confirm with the “Order with payment” button. Creating an account is free.',
  },
  tr: {
    planTaglines: { starter: 'Sakin bir başlangıç için', pro: 'Tam işletme için', enterprise: 'Birden fazla şube için' },
    socialGoogle: 'Google ile devam et',
    features: [
      '7 gün tam erişim',
      'Kredi kartı ekleyin, istediğiniz zaman iptal edin',
      '7 gün sonra otomatik tahsilat',
      'Rezervasyonları otomatik kabul edin',
    ],
    defaultSlug: 'restoranim',
    demoRestaurant: 'demo-restoran',
    cancelledError: 'Ödeme iptal edildi. Seçtiğiniz planla denemeyi istediğiniz zaman yeniden başlatabilirsiniz.',
    errConsentOAuth: 'Lütfen önce Kullanım Koşulları, Gizlilik Politikası ve VİS sözleşmesini kabul edin.',
    errOAuthFailed: 'OAuth başarısız oldu',
    errCheckoutFailed: 'Ödeme başlatılamadı',
    errCheckout: 'Ödeme hatası',
    errConsent: 'Lütfen Kullanım Koşulları, Gizlilik Politikası ve Veri İşleme Sözleşmesini kabul edin.',
    errB2B: 'Lütfen bir işletme (restoran işletmecisi) olarak hareket ettiğinizi onaylayın.',
    errSignupFailed: 'Kayıt başarısız oldu',
    errEmailExists: 'Bu e-posta adresi zaten kayıtlı.',
    brandHeading1: 'Restoranınız için',
    brandHeading2: 'yönetim çözümü.',
    brandSub: 'Rezervasyonlar, siparişler ve personel — tek bir platformda.',
    planSuffix: ' planı',
    change: 'Değiştir',
    perMonth: '/ay',
    perMonthYearly: ' (yıllık)',
    trialBlurb: '7 gün ücretsiz deneyin. Kartınız ödeme sırasında kaydedilir, ancak yalnızca 8. günde tahsil edilir.',
    footerCopy: '© Rezaro',
    alreadyAccount: 'Zaten hesabınız var mı?',
    signIn: 'Giriş yap',
    stepLabel: 'Kayıt',
    formHeading1: 'Bize restoranınızdan',
    formHeading2: 'bahsedin.',
    formSub: 'Hesabınızı birkaç adımda oluşturun.',
    preConsentPre: 'Şunları kabul ediyorum:',
    preConsentMid: ',',
    preConsentAnd: 've',
    preConsentPost: '.',
    socialDisabledTitle: 'Lütfen önce Kullanım Koşullarını kabul edin',
    or: 'veya',
    emailCta: 'E-posta ile kayıt ol',
    emailLabel: 'E-posta adresi',
    emailPlaceholder: 'siz@email.com',
    passwordLabel: 'Parola',
    passwordHint: '(en az 8 karakter)',
    restaurantNameLabel: 'Restoran adı',
    restaurantNamePlaceholder: 'Restoranın adı',
    addressLabel: 'Restoran adresi',
    addressPlaceholder: 'Adres, şehir',
    phoneLabel: 'Telefon',
    phonePlaceholder: '030 12345678',
    websiteLabel: 'Restoran web sitesi',
    websiteHint: '(isteğe bağlı)',
    websitePlaceholder: 'www.restoranim.com',
    consentPre: 'Şunları okudum:',
    consentMid1: ',',
    consentMid2: ',',
    consentMid3: 've',
    consentPost: 've bunları kabul ediyorum.',
    b2bPre: 'Bir',
    b2bStrong: 'işletme (restoran işletmecisi)',
    b2bPost: 'olarak hareket ettiğimi onaylıyorum. Rezaro bir B2B hizmetidir — bu nedenle §§ 312g, 355 BGB uyarınca cayma hakkı geçerli değildir. Bunun yerine 7 günlük ücretsiz deneme süresi geçerlidir.',
    submitLoading: 'Hesap oluşturuluyor...',
    submit: 'Ücretsiz hesap oluştur',
    submitBlurb: 'Kartınız yalnızca ücretli bir plan seçip „Ücretli sipariş ver" düğmesiyle onayladıktan sonra tahsil edilir. Hesap oluşturmak ücretsizdir.',
  },
} as const

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  )
}

function toSlug(name: string, fallback: string): string {
  return name
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || fallback
}

export default function SignupPage({ lang = 'de' }: { lang?: SiteLang }) {
  const t = T[lang]
  const router = useRouter()
  const searchParams = useSearchParams()
  const planParam = searchParams.get('plan')
  const billingParam = searchParams.get('billing')
  const cancelled = searchParams.get('cancelled') === '1'

  const PLAN_CARDS: Record<SelectedPlan, PlanCard> = {
    starter: { id: 'starter', name: 'Starter', monthly: 29, tagline: t.planTaglines.starter, icon: Sparkles },
    pro: { id: 'pro', name: 'Pro', monthly: 69, tagline: t.planTaglines.pro, icon: Zap },
    enterprise: { id: 'enterprise', name: 'Premium', monthly: 119, tagline: t.planTaglines.enterprise, icon: Building2 },
  }

  const selectedPlan: SelectedPlan =
    planParam === 'starter' || planParam === 'pro' || planParam === 'enterprise' ? planParam : 'pro'
  const selectedBilling: SelectedBilling = billingParam === 'yearly' ? 'yearly' : 'monthly'
  const planCard = PLAN_CARDS[selectedPlan]

  const [showEmailForm, setShowEmailForm] = useState(false)
  const [restaurantName, setRestaurantName] = useState('')
  const [address, setAddress] = useState('')
  const [phone, setPhone] = useState('')
  const [phoneCode, setPhoneCode] = useState('+49')
  const [website, setWebsite] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [confirmedB2B, setConfirmedB2B] = useState(false)
  const [error, setError] = useState<string | null>(
    cancelled
      ? t.cancelledError
      : null
  )
  const [loading, setLoading] = useState(false)

  const billedPrice = selectedBilling === 'yearly' ? Math.round(planCard.monthly * 0.8) : planCard.monthly

  async function handleSocial(provider: 'google' | 'facebook') {
    setError(null)
    if (!acceptedTerms) {
      setError(t.errConsentOAuth)
      // Open the e-mail form so the AGB checkbox becomes visible
      setShowEmailForm(true)
      return
    }
    try {
      // Stash AGB consent + selected plan so /onboarding/complete-profile
      // can persist them after OAuth + kick off Stripe Checkout.
      const consent = {
        agb_v: '1.0',
        datenschutz_v: '1.0',
        avv_v: '1.0',
        accepted_at: new Date().toISOString(),
        accepted_via: 'web-oauth-signup',
        plan: selectedPlan,
        billing: selectedBilling,
      }
      try { sessionStorage.setItem('rzr-oauth-pending-profile', JSON.stringify(consent)) } catch { /* ignore */ }

      const supabase = createClient()
      const profileNext = `/onboarding/complete-profile?plan=${selectedPlan}&billing=${selectedBilling}`
      // Native app: open Google in a system Custom Tab, return via the
      // rezaro:// deep link (handled in NativeBridge → exchangeCodeForSession).
      const cap = typeof window !== 'undefined' ? window.Capacitor : undefined
      if (cap?.isNativePlatform?.()) {
        const { data, error: nativeErr } = await supabase.auth.signInWithOAuth({
          provider,
          options: {
            redirectTo: `rezaro://auth/callback?next=${encodeURIComponent(profileNext)}`,
            skipBrowserRedirect: true,
          },
        })
        if (nativeErr) throw nativeErr
        if (data?.url) await cap.Plugins?.Browser?.open({ url: data.url })
        return
      }
      const { error: err } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(profileNext)}`,
        },
      })
      if (err) throw err
    } catch (err) {
      setError(err instanceof Error ? err.message : t.errOAuthFailed)
    }
  }

  async function startCheckout(slug: string): Promise<boolean> {
    try {
      const r = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: selectedPlan, billing: selectedBilling, restaurant_slug: slug }),
      })
      const data = await r.json() as { url?: string; error?: string }
      if (r.ok && data.url) {
        window.location.assign(data.url)
        return true
      }
      // Stripe not configured yet (503) → fall back to the onboarding
      // flow so the user can still finish signup. Webhook will sync later.
      if (r.status === 503) {
        router.push(`/${slug}/onboarding/opening-hours`)
        return true
      }
      throw new Error(data.error ?? t.errCheckoutFailed)
    } catch (err) {
      setError(err instanceof Error ? err.message : t.errCheckout)
      return false
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!acceptedTerms) {
      setError(t.errConsent)
      return
    }
    if (!confirmedB2B) {
      setError(t.errB2B)
      return
    }
    setLoading(true)
    try {
      const slug = toSlug(restaurantName, t.defaultSlug) || t.demoRestaurant
      const supabase = createClient()
      const fullPhone = phoneCode + ' ' + phone

      // 1) Auth: signUp triggers create_user_restaurant() in DB → row auto-created with metadata
      const consentRecord = {
        agb_v: '1.0',
        datenschutz_v: '1.0',
        avv_v: '1.0',
        accepted_at: new Date().toISOString(),
        accepted_via: 'web-signup',
      }
      const { data: signUpData, error: signUpErr } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            restaurant_name: restaurantName,
            restaurant_slug: slug,
            terms_consent: consentRecord,
          },
        },
      })
      if (signUpErr) throw signUpErr

      // 2) Update restaurant with extra contact info + selected plan
      const ownerId = signUpData.user?.id
      let actualSlug = slug
      if (ownerId) {
        // Wait briefly for trigger to commit
        await new Promise(r => setTimeout(r, 400))
        // Find the restaurant the trigger created (in case slug was suffixed for uniqueness)
        const { data: rest } = await supabase
          .from('restaurants')
          .select('slug')
          .eq('owner_id', ownerId)
          .maybeSingle()
        if (rest?.slug) {
          actualSlug = rest.slug
          await supabase
            .from('restaurants')
            .update({
              email,
              phone: fullPhone,
              address,
              website: website || null,
              subscription_plan: selectedPlan,
              subscription_billing: selectedBilling,
            })
            .eq('owner_id', ownerId)
        }
      }

      // 3) Off to Stripe Checkout. startCheckout sets its own error and
      // falls back to opening-hours when Stripe isn't configured yet.
      const handled = await startCheckout(actualSlug)
      if (!handled) {
        router.push(`/${actualSlug}/onboarding/plan`)
        router.refresh()
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : t.errSignupFailed
      setError(msg.includes('User already registered') ? t.errEmailExists : msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen" style={{ fontFamily: 'var(--font-pub)' }}>

      {/* Left — brand panel */}
      <div className="hidden lg:flex lg:w-2/5 flex-col justify-between p-12 bg-pub-dark">
        <div>
          <div className="mb-14">
            <RezaroLogo variant="dark" size="md" showWordmark showTagline />
          </div>

          <h2 className="text-4xl font-light text-white leading-tight mb-4"
            style={{ fontFamily: 'var(--font-display)' }}>
            {t.brandHeading1}<br />
            <em className="not-italic font-semibold">{t.brandHeading2}</em>
          </h2>
          <p className="text-[13px] text-white/40 mb-8 leading-relaxed">
            {t.brandSub}
          </p>

          {/* Selected plan summary */}
          <div className="mb-8 rounded-2xl border border-white/15 bg-white/[0.04] p-5">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-pub-accent/15 text-pub-accent">
                  <planCard.icon className="h-4 w-4" strokeWidth={1.7} />
                </span>
                <div>
                  <p className="text-[14px] font-semibold text-white">{planCard.name}{t.planSuffix}</p>
                  <p className="text-[11px] text-white/50">{planCard.tagline}</p>
                </div>
              </div>
              <Link href={lp('/pricing', lang) + '?from=signup'}
                className="text-[11px] text-pub-accent hover:text-white transition-colors underline-offset-2 hover:underline">
                {t.change}
              </Link>
            </div>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-[28px] font-semibold text-white leading-none"
                style={{ fontFamily: 'var(--font-display)' }}>
                {billedPrice} €
              </span>
              <span className="text-[12px] text-white/60">
                {t.perMonth}{selectedBilling === 'yearly' ? t.perMonthYearly : ''}
              </span>
            </div>
            <p className="text-[11px] text-white/50 leading-relaxed">
              {t.trialBlurb}
            </p>
          </div>

          <ul className="space-y-3.5">
            {t.features.map((f) => (
              <li key={f} className="flex items-center gap-3">
                <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-pub-accent/20">
                  <Check className="h-3 w-3 text-pub-accent" />
                </div>
                <span className="text-[13px] text-white/70">{f}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-wrap gap-4 text-[12px] text-white/25">
          <span>{t.footerCopy}</span>
          {[
            { label: 'AGB', href: '/agb' },
            { label: 'Datenschutz', href: '/datenschutz' },
            { label: 'Impressum', href: '/impressum' },
            { label: 'AVV', href: '/avv' },
          ].map(({ label, href }) => (
            <Link key={label} href={href} className="hover:text-white/60 transition-colors">{label}</Link>
          ))}
        </div>
      </div>

      {/* Right — form. my-auto instead of justify-center so a form taller
          than the viewport (phones, open keyboard) scrolls instead of being
          clipped at the top — see signin for the full story. */}
      <div className="flex flex-1 flex-col items-center px-8 py-12 bg-pub-bg">
        <div className="w-full max-w-md my-auto">
          {/* Mobile brand header — desktop shows the logo in the left panel */}
          <div className="lg:hidden mb-8">
            <RezaroLogo variant="light" size="md" />
          </div>
          <div className="flex justify-end mb-10">
            <Link href={lp('/signin', lang)} className="text-[13px] text-pub-muted hover:text-pub-ink transition-colors">
              {t.alreadyAccount}{' '}
              <span className="font-semibold text-pub-accent">{t.signIn}</span>
            </Link>
          </div>

          {/* Steps */}
          <div className="flex items-center gap-1.5 mb-8">
            {[1, 2, 3, 4].map((n, i) => (
              <div key={n} className="flex items-center gap-1.5">
                <div className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${n === 1 ? 'bg-pub-accent text-white' : 'bg-pub-rule text-pub-muted'}`}>
                  {n === 4 ? '✓' : n}
                </div>
                {i < 3 && <div className="h-px w-5 bg-pub-rule" />}
              </div>
            ))}
          </div>

          <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-pub-muted/50 mb-1">{t.stepLabel}</p>
          <h1 className="text-3xl font-light text-pub-ink mb-1" style={{ fontFamily: 'var(--font-display)' }}>
            {t.formHeading1}<br />{t.formHeading2}
          </h1>
          <p className="text-[13px] text-pub-muted mb-5">{t.formSub}</p>

          {/* Pre-OAuth AGB consent (DSGVO requires acceptance BEFORE third-party redirect) */}
          <label className="flex items-start gap-2 mb-4 cursor-pointer">
            <input type="checkbox" checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-0.5 h-4 w-4 flex-shrink-0 accent-pub-accent cursor-pointer" />
            <span className="text-[11px] leading-relaxed text-pub-muted">
              {t.preConsentPre}{' '}
              <Link href="/agb" target="_blank" className="text-pub-accent underline hover:no-underline">AGB</Link>,
              {' '}{t.preConsentMid}{' '}
              <Link href="/datenschutz" target="_blank" className="text-pub-accent underline hover:no-underline">Datenschutz&shy;erklärung</Link>
              {' '}{t.preConsentAnd}{' '}
              <Link href="/avv" target="_blank" className="text-pub-accent underline hover:no-underline">AVV</Link>
              {' '}{t.preConsentPost}
            </span>
          </label>

          {/* Social */}
          <div className="space-y-2 mb-4">
            {[{ id: 'google' as const, label: t.socialGoogle, Icon: GoogleIcon }].map(({ id, label, Icon }) => (
              <button key={id} type="button"
                onClick={() => handleSocial(id)}
                disabled={!acceptedTerms}
                title={!acceptedTerms ? t.socialDisabledTitle : undefined}
                className="w-full flex items-center justify-center gap-2.5 rounded-lg border border-pub-rule bg-pub-surface py-2.5 text-[13px] font-medium text-pub-ink hover:bg-pub-bg hover:border-pub-ink/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                <Icon />
                <span>{label}</span>
              </button>
            ))}
          </div>
          {error && !showEmailForm && (
            <p className="text-[11px] text-red-600 mb-3 text-center">{error}</p>
          )}

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-pub-rule" />
            <span className="text-[11px] text-pub-muted/50">{t.or}</span>
            <div className="flex-1 h-px bg-pub-rule" />
          </div>

          {!showEmailForm ? (
            <button type="button" onClick={() => setShowEmailForm(true)}
              className="w-full h-10 rounded-full border border-pub-rule bg-pub-surface text-[13px] font-semibold text-pub-ink hover:bg-pub-bg transition-colors mb-6">
              {t.emailCta}
            </button>
          ) : (
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-[12px] font-medium text-pub-ink">{t.emailLabel}</label>
                <input id="email" type="email" required autoComplete="email" placeholder={t.emailPlaceholder}
                  value={email} onChange={e => setEmail(e.target.value)}
                  className="h-10 rounded-lg border border-pub-rule bg-pub-surface px-3.5 text-[13px] text-pub-ink placeholder:text-pub-muted/40 focus:outline-none focus:ring-2 focus:ring-pub-accent/30 focus:border-pub-accent/50 transition-colors" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="password" className="text-[12px] font-medium text-pub-ink">{t.passwordLabel} <span className="font-normal text-pub-muted/50">{t.passwordHint}</span></label>
                <input id="password" type="password" required minLength={8} autoComplete="new-password" placeholder="••••••••"
                  value={password} onChange={e => setPassword(e.target.value)}
                  className="h-10 rounded-lg border border-pub-rule bg-pub-surface px-3.5 text-[13px] text-pub-ink placeholder:text-pub-muted/40 focus:outline-none focus:ring-2 focus:ring-pub-accent/30 focus:border-pub-accent/50 transition-colors" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="restaurant-name" className="text-[12px] font-medium text-pub-ink">{t.restaurantNameLabel}</label>
                <input id="restaurant-name" type="text" required autoComplete="organization" placeholder={t.restaurantNamePlaceholder}
                  value={restaurantName} onChange={(e) => setRestaurantName(e.target.value)}
                  className="h-10 rounded-lg border border-pub-rule bg-pub-surface px-3.5 text-[13px] text-pub-ink placeholder:text-pub-muted/40 focus:outline-none focus:ring-2 focus:ring-pub-accent/30 focus:border-pub-accent/50 transition-colors" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="restaurant-address" className="text-[12px] font-medium text-pub-ink">{t.addressLabel}</label>
                <input id="restaurant-address" type="text" required autoComplete="street-address" placeholder={t.addressPlaceholder}
                  value={address} onChange={e => setAddress(e.target.value)}
                  className="h-10 rounded-lg border border-pub-rule bg-pub-surface px-3.5 text-[13px] text-pub-ink placeholder:text-pub-muted/40 focus:outline-none focus:ring-2 focus:ring-pub-accent/30 focus:border-pub-accent/50 transition-colors" />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[12px] font-medium text-pub-ink">{t.phoneLabel}</label>
                <div className="flex gap-2">
                  <div className="relative">
                    <select value={phoneCode} onChange={e => setPhoneCode(e.target.value)}
                      className="h-10 appearance-none rounded-lg border border-pub-rule bg-pub-surface pl-3 pr-7 text-[13px] text-pub-ink focus:outline-none focus:ring-2 focus:ring-pub-accent/30">
                      <option value="+49">+49 🇩🇪</option>
                      <option value="+43">+43 🇦🇹</option>
                      <option value="+41">+41 🇨🇭</option>
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-pub-muted" />
                  </div>
                  <input placeholder={t.phonePlaceholder} value={phone} onChange={e => setPhone(e.target.value)}
                    className="flex-1 h-10 rounded-lg border border-pub-rule bg-pub-surface px-3.5 text-[13px] text-pub-ink placeholder:text-pub-muted/40 focus:outline-none focus:ring-2 focus:ring-pub-accent/30 focus:border-pub-accent/50 transition-colors" />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[12px] font-medium text-pub-ink">{t.websiteLabel} <span className="font-normal text-pub-muted/50">{t.websiteHint}</span></label>
                <input placeholder={t.websitePlaceholder} value={website} onChange={e => setWebsite(e.target.value)}
                  className="h-10 rounded-lg border border-pub-rule bg-pub-surface px-3.5 text-[13px] text-pub-ink placeholder:text-pub-muted/40 focus:outline-none focus:ring-2 focus:ring-pub-accent/30 focus:border-pub-accent/50 transition-colors" />
              </div>

              <label className="flex items-start gap-2.5 cursor-pointer pt-1">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 flex-shrink-0 accent-pub-accent cursor-pointer"
                />
                <span className="text-[12px] leading-relaxed text-pub-muted">
                  {t.consentPre}{' '}
                  <Link href="/agb" target="_blank" className="text-pub-accent underline hover:no-underline">AGB</Link>,
                  {' '}{t.consentMid1}{' '}
                  <Link href="/datenschutz" target="_blank" className="text-pub-accent underline hover:no-underline">Datenschutz&shy;erklärung</Link>,
                  {' '}{t.consentMid2}{' '}
                  <Link href="/avv" target="_blank" className="text-pub-accent underline hover:no-underline">Auftragsverarbeitungs&shy;vertrag</Link>
                  {' '}{t.consentMid3}{' '}
                  <Link href="/widerrufsbelehrung" target="_blank" className="text-pub-accent underline hover:no-underline">Widerrufs&shy;belehrung</Link>
                  {' '}{t.consentPost}
                </span>
              </label>

              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmedB2B}
                  onChange={(e) => setConfirmedB2B(e.target.checked)}
                  className="mt-0.5 h-4 w-4 flex-shrink-0 accent-pub-accent cursor-pointer"
                />
                <span className="text-[12px] leading-relaxed text-pub-muted">
                  {t.b2bPre} <strong className="text-pub-ink">{t.b2bStrong}</strong> {t.b2bPost}
                </span>
              </label>

              {error && <p className="text-[12px] text-red-600">{error}</p>}

              <button type="submit" disabled={loading || !acceptedTerms || !confirmedB2B}
                className="w-full h-10 rounded-full bg-pub-accent text-[13px] font-semibold text-white hover:bg-pub-accent-dark transition-colors disabled:opacity-60">
                {loading ? t.submitLoading : t.submit}
              </button>

              <p className="text-[11px] text-pub-muted/70 text-center leading-relaxed">
                {t.submitBlurb}
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
